/**
 * Health-profile display units. Stored values stay centimeters and kilograms.
 * US defaults to imperial; a saved choice overrides the region default.
 */

import { useRef, useState } from "react";

export const UNIT_SYSTEM_STORAGE_KEY = "mc_unit_system";

const CM_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;
const LB_PER_KG = 2.2046226218;

export function defaultUnitSystem(region) {
  return String(region ?? "").trim().toLowerCase() === "us" ? "imperial" : "metric";
}

export function resolveUnitSystem(region, stored) {
  if (stored === "metric" || stored === "imperial") return stored;
  return defaultUnitSystem(region);
}

export function storedUnitSystem(region) {
  let stored = null;
  try {
    stored = window.localStorage.getItem(UNIT_SYSTEM_STORAGE_KEY);
  } catch {
    stored = null;
  }
  return resolveUnitSystem(region, stored);
}

function formatMeasure(value) {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function parseMeasure(text) {
  const raw = String(text ?? "").trim();
  if (!raw) return { empty: true, incomplete: false, value: null };
  if (!/^\d*\.?\d*$/.test(raw) || raw === "." || raw.endsWith(".")) {
    return { empty: false, incomplete: true, value: null };
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) return { empty: false, incomplete: true, value: null };
  return { empty: false, incomplete: false, value };
}

export function cmToFeetInches(cmText) {
  const parsed = parseMeasure(cmText);
  if (parsed.empty || parsed.incomplete || parsed.value == null) {
    return { feet: "", inches: "" };
  }
  const totalInches = Math.round(parsed.value / CM_PER_INCH);
  const feet = Math.floor(totalInches / INCHES_PER_FOOT);
  const inches = totalInches - feet * INCHES_PER_FOOT;
  return { feet: String(feet), inches: String(inches) };
}

/** @returns {string | null} centimeters, "" when blank, null while the draft is incomplete */
export function feetInchesToCm(feetText, inchesText) {
  const feet = parseMeasure(feetText);
  const inches = parseMeasure(inchesText);
  if (feet.incomplete || inches.incomplete) return null;
  if (feet.empty && inches.empty) return "";
  const feetValue = feet.empty ? 0 : feet.value;
  const inchesValue = inches.empty ? 0 : inches.value;
  return formatMeasure((feetValue * INCHES_PER_FOOT + inchesValue) * CM_PER_INCH);
}

export function kgToLb(kgText) {
  const parsed = parseMeasure(kgText);
  if (parsed.empty || parsed.incomplete || parsed.value == null) return "";
  return formatMeasure(parsed.value * LB_PER_KG);
}

/** @returns {string | null} kilograms, "" when blank, null while the draft is incomplete */
export function lbToKg(lbText) {
  const parsed = parseMeasure(lbText);
  if (parsed.incomplete) return null;
  if (parsed.empty || parsed.value == null) return "";
  return formatMeasure(parsed.value / LB_PER_KG);
}

export function draftFromMetric(system, heightCm, weightKg) {
  const height = heightCm == null ? "" : String(heightCm);
  const weight = weightKg == null ? "" : String(weightKg);
  if (system === "imperial") {
    const { feet, inches } = cmToFeetInches(height);
    return { feet, inches, height: "", weight: kgToLb(weight) };
  }
  return { feet: "", inches: "", height, weight };
}

export function heightCmFromDraft(system, draft) {
  if (system === "metric") return draft.height ?? "";
  return feetInchesToCm(draft.feet, draft.inches);
}

export function weightKgFromDraft(system, draft) {
  if (system === "metric") return draft.weight ?? "";
  return lbToKg(draft.weight);
}

/**
 * Local ft/in/lb (or cm/kg) text that does not round-trip on each keystroke.
 * Canonical height_cm / weight_kg update when the draft is a complete number.
 */
export function useMeasurementDraft(system, heightCm, weightKg, onChange) {
  const height = heightCm == null ? "" : String(heightCm);
  const weight = weightKg == null ? "" : String(weightKg);
  const [draft, setDraft] = useState(() => draftFromMetric(system, height, weight));
  const draftRef = useRef(draft);
  const written = useRef({ system, height, weight });

  if (
    written.current.system !== system ||
    written.current.height !== height ||
    written.current.weight !== weight
  ) {
    written.current = { system, height, weight };
    const next = draftFromMetric(system, height, weight);
    draftRef.current = next;
    setDraft(next);
  }

  const edit = (partial) => {
    const next = { ...draftRef.current, ...partial };
    draftRef.current = next;
    setDraft(next);
    const nextHeight = heightCmFromDraft(system, next);
    const nextWeight = weightKgFromDraft(system, next);
    const patch = {};
    if (nextHeight != null && nextHeight !== height) patch.height_cm = nextHeight;
    if (nextWeight != null && nextWeight !== weight) patch.weight_kg = nextWeight;
    if (patch.height_cm == null && patch.weight_kg == null) return;
    written.current = {
      system,
      height: patch.height_cm ?? height,
      weight: patch.weight_kg ?? weight,
    };
    onChange(patch);
  };

  return {
    draft,
    setFeet: (feet) => edit({ feet }),
    setInches: (inches) => edit({ inches }),
    setHeight: (text) => edit({ height: text }),
    setWeight: (text) => edit({ weight: text }),
  };
}
